import type ExpiresAfter from "../../domain/ExpiresAfter";
import {maxExpiryDays, planEntitlements} from "../../pricing/planEntitlements";
import type {PaidPlanId} from "../../pricing/planCatalogue";
import {
	allowedExpiryValues, expiryDays, expiryOptions, isIndefiniteExpiry, isPaid, isSubDayExpiry,
	nextUpgradePlan, type Plan,
} from "../gating";
import {TRANSFER_OPTIONS, type OptionKey} from "./createFormLogic";

/**
 * The send screen's upsell card (kitecorp/transferify-ui#2278, rewritten by #2514): which tier a wall
 * sells, and the figures its one line quotes.
 *
 * Pure domain, no React (epic #26). It restates no limit and no gate: every number comes from the plan
 * catalogue through `pricing/planEntitlements`, and every TIER comes from the same function the locked
 * control's own pill asks — `TRANSFER_OPTIONS[].unlockTier` for the option rows, `expiryOptions`'
 * `unlockTier` for the retention rungs. So a card can never advertise a window the api does not enforce,
 * nor a tier that would not actually unlock what the sender just touched.
 *
 * <b>The target is the tier that unlocks THAT CONTROL, not the next tier up</b> (owner 2026-09-17,
 * after testing #2278 on prod as a Basic sender: "we should show whatever the user has clicked"). The
 * shipped ladder sold one step at a time, which meant a Basic sender reaching for Zero-Knowledge was
 * offered Plus — a tier that does not carry it — and a free sender reaching for the thirty-day rung was
 * offered Basic's seven. The two CEILING rows below are the exception and keep the one-step ladder,
 * because nothing there is locked: the sender asked for more than their own plan's maximum, and the
 * cheapest tier that gives more is the honest answer.
 *
 * <b>A filled recipient list is deliberately NOT a wall here</b> (owner 2026-09-17, #2518). The Options
 * sheet that list lives in already carries its own Upgrade action for every tier below Premium, so a
 * card about the same cap would be a second ask for one limit on the surface that already asks. Removed
 * for every tier rather than narrowed to one — do not re-add a trigger for it.
 */
export type UpsellTrigger =
	/** The sender toggled a plan-gated option row their own tier keeps locked. */
	| {control: "option"; option: OptionKey}
	/** The sender reached for a retention rung their own plan's window does not admit. */
	| {control: "expiry"; rung: ExpiresAfter}
	/** The sender explicitly chose the longest retention their own plan allows. */
	| {control: "maxExpiry"};

/**
 * What the card says, by wall. A union rather than one wide record with optional figures: each wall
 * quotes exactly one number, and a shared record would let a card render the other wall's.
 */
export type UpsellCardModel =
	| {trigger: "password"; target: PaidPlanId}
	/** `days` is null for the indefinite rung, which has no day count to quote. */
	| {trigger: "expiry"; target: PaidPlanId; days: number | null}
	/** `days` is null where the target's links never expire, which is the Premium row's whole offer. */
	| {trigger: "maxExpiry"; target: PaidPlanId; fromDays: number; days: number | null}
	/**
	 * Every other locked option, sold by ONE generic row (owner 2026-09-17): the card's title is the
	 * control's own catalog label and its line says what tier includes it. `option` travels so the
	 * React layer can look that label up — no per-feature copy matrix, in any language.
	 */
	| {trigger: "option"; option: OptionKey; target: PaidPlanId};

/** The longest DAY retention a plan admits, or null for a plan whose window has no day rung. */
function longestDayRung(plan: Plan): ExpiresAfter | null {
	const allowed = allowedExpiryValues(plan);
	return allowed.length === 0 ? null : allowed[allowed.length - 1];
}

/**
 * Whether this retention is the longest one the sender's OWN plan allows — the "I have hit my ceiling"
 * signal the paid rows fire on.
 *
 * Day rungs only: the sub-day steps are shorter than every advertised window, and the indefinite rung
 * has no day count at all, so neither can be anyone's ceiling in the sense this card sells past.
 */
export function isPlanMaxExpiry(plan: Plan, value: ExpiresAfter): boolean {
	if (isIndefiniteExpiry(value) || isSubDayExpiry(value)) {
		return false;
	}
	const longest = longestDayRung(plan);
	return longest !== null && expiryDays(value) === expiryDays(longest);
}

/**
 * The card for a wall the sender just met, or null when there is nothing to sell.
 *
 * Null for every control the sender's own tier already holds, which is what makes Premium cardless
 * without a tier check of its own: it holds every option and its window admits every rung. Keeping
 * that decision here means the panel arms a wall and this module decides whether it sells anything.
 */
export function upsellCard(plan: Plan, trigger: UpsellTrigger | null): UpsellCardModel | null {
	if (trigger === null) {
		return null;
	}
	switch (trigger.control) {
		case "option":
			return lockedOptionCard(plan, trigger.option);
		case "expiry":
			return lockedExpiryCard(plan, trigger.rung);
		case "maxExpiry":
			return chosenCeilingCard(plan);
	}
}

/**
 * A plan-gated option row, sold by the tier its own locked pill names.
 *
 * The password row keeps its approved copy and therefore its own trigger; every other option takes the
 * generic row. Both read `TRANSFER_OPTIONS` rather than a table of their own — the card asking a
 * different source than the pill is precisely how a row could advertise a tier that does not unlock it.
 */
function lockedOptionCard(plan: Plan, option: OptionKey): UpsellCardModel | null {
	const def = TRANSFER_OPTIONS.find(candidate => candidate.key === option);
	if (def === undefined || def.canUse(plan)) {
		return null;
	}
	return option === "password"
		? {trigger: "password", target: def.unlockTier}
		: {trigger: "option", option, target: def.unlockTier};
}

/**
 * A retention rung above the sender's window, sold by the tier that admits THAT rung and quoting the
 * day count the sender actually touched.
 *
 * `expiryOptions` is the picker's own list, so the card and the rung's gold badge can never name
 * different tiers — the ui#1203 defect, one level up.
 */
function lockedExpiryCard(plan: Plan, rung: ExpiresAfter): UpsellCardModel | null {
	const target = expiryOptions(plan).find(choice => choice.value === rung)?.unlockTier;
	if (target === undefined || target === null) {
		return null;
	}
	// A locked SUB-DAY rung has no day count and is not an indefinite one either, so neither line the
	// card carries can describe it. Unreachable with the shipped catalogue — every tier's policy sets
	// `allowsSubDay` — and refused rather than defaulted, because the null day count means "never
	// expires" further down and that is the one sentence a fifteen-minute link must not carry.
	if (isSubDayExpiry(rung)) {
		return null;
	}
	return {trigger: "expiry", target, days: isIndefiniteExpiry(rung) ? null : expiryDays(rung)};
}

/**
 * The sender chose their own plan's longest retention — nothing is locked, so the honest offer is the
 * cheapest tier that gives more, which is the next one up. Unchanged by #2514 on the owner's word.
 */
function chosenCeilingCard(plan: Plan): UpsellCardModel | null {
	const target = nextUpgradePlan(plan);
	if (target === null || !isPaid(plan)) {
		return null;
	}
	// Premium's offer is that links never expire, and its day window is Plus's own thirty — so the row
	// asks the catalogue what the target GIVES rather than comparing two day counts, which would
	// advertise "up to 30 days" to a sender already on 30.
	const days = planEntitlements(target).expiryPolicy.allowsNever ? null : maxExpiryDays(target);
	return {trigger: "maxExpiry", target, fromDays: maxExpiryDays(plan), days};
}
